import React, { useState, useEffect } from 'react';
import { authAPI } from '../api';

function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setMessage('');
    try {
      console.log('Admin: Loading users...');
      const response = await authAPI.getUsers();
      console.log('Admin: Response received:', response.data);
      if (response.data.success) {
        console.log(`Admin: Loaded ${response.data.users.length} users`);
        setUsers(response.data.users);
      } else {
        setMessage('Failed to load users: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Admin: Error loading users:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to load users';
      setMessage('Error: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuota = async (username, tier) => {
    setUpdating(username);
    setMessage('');
    
    try {
      console.log(`Admin: Updating ${username} to ${tier} tier...`);
      const response = await authAPI.updateUserQuota(username, tier);
      console.log('Admin: Update response:', response.data);
      if (response.data.success) {
        setMessage(`✓ ${response.data.message}`);
        loadUsers();
      } else {
        setMessage(`✗ ${response.data.error || 'Update failed'}`);
      }
    } catch (error) {
      console.error('Admin: Error updating quota:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to update quota';
      setMessage(`✗ ${errorMsg}`);
    } finally {
      setUpdating(null);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getTierColor = (tier) => {
    const colors = {
      free: '#8a8a9a',
      basic: '#6da88d',
      pro: '#6a92b8',
      enterprise: '#a67c52'
    };
    return colors[tier] || colors.free;
  };

  return (
    <div className="container">
      <h1 className="mb-3">Admin Panel</h1>
      
      {message && (
        <div className={`alert ${message.startsWith('✓') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      <div className="card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>User Management</h3>
          <button onClick={loadUsers} className="btn btn-secondary">
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : users.length === 0 ? (
          <p className="text-muted">No users found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Current Tier</th>
                  <th>Storage Used</th>
                  <th>Usage %</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.username}>
                    <td style={{ fontWeight: '600' }}>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span
                        className="badge"
                        style={{ 
                          backgroundColor: getTierColor(user.storage_tier),
                          textTransform: 'uppercase'
                        }}
                      >
                        {user.storage_tier}
                      </span>
                    </td>
                    <td>
                      {formatBytes(user.storage_used)} / {formatBytes(user.storage_quota)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '100px',
                          height: '8px',
                          backgroundColor: '#dfe7fd',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${Math.min(user.storage_percentage, 100)}%`,
                            height: '100%',
                            backgroundColor: user.storage_percentage > 90 ? '#c96a7a' : '#6da88d',
                            transition: 'width 0.3s'
                          }}></div>
                        </div>
                        <span style={{ fontSize: '12px', color: '#7f7f94' }}>
                          {Math.round(user.storage_percentage)}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <select
                          value={user.storage_tier}
                          onChange={(e) => handleUpdateQuota(user.username, e.target.value)}
                          disabled={updating === user.username}
                          className="form-control"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: '13px',
                            width: 'auto',
                            minWidth: '120px'
                          }}
                        >
                          <option value="free">Free (100 MB)</option>
                          <option value="basic">Basic (1 GB)</option>
                          <option value="pro">Pro (10 GB)</option>
                          <option value="enterprise">Enterprise (1 TB)</option>
                        </select>
                        {updating === user.username && (
                          <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card mt-3">
        <h3 className="mb-2">Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{users.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Free Tier</div>
            <div className="stat-value">
              {users.filter(u => u.storage_tier === 'free').length}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Paid Tier</div>
            <div className="stat-value">
              {users.filter(u => u.storage_tier !== 'free').length}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Storage Used</div>
            <div className="stat-value">
              {formatBytes(users.reduce((sum, u) => sum + u.storage_used, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
